import { Helmet } from "react-helmet-async";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useSiteContent } from "@/context/SiteContentContext";

const TITLE_MAX = 60;
const DESC_MAX = 160;

/** Trim to a word boundary so snippets never get cut mid-word by search engines. */
function clamp(text, max) {
  const s = String(text || "").replace(/\s+/g, " ").trim();
  if (s.length <= max) return s;
  const cut = s.slice(0, max - 1);
  const at = cut.lastIndexOf(" ");
  return `${(at > max * 0.6 ? cut.slice(0, at) : cut).replace(/[\s,.;:–-]+$/, "")}…`;
}

/**
 * Reusable SEO component. Every page should render one <SEO /> with a unique
 * title and description. Uses react-helmet-async so it works with SSR later.
 *
 * Enhancements:
 *  - hreflang alternates (en, ar, x-default) for bilingual reach
 *  - keywords meta support
 *  - author + theme-color for richer previews
 *  - Twitter site handle + creator
 */
export function SEO({
  title,
  description,
  path,
  image,
  type = "website",
  noindex = false,
  jsonLd,
  keywords,
  publishedTime,
  modifiedTime,
}) {
  const { i18n } = useTranslation();
  const { content } = useSiteContent();
  // Admin-managed SEO block (Settings → SEO) wins over the hardcoded fallbacks.
  const seo = content?.seo || {};
  const lang = i18n.language || "en";
  const isAr = lang === "ar";
  const brand = seo.site_name || "The Saudi Gate";
  const homeTitle = (isAr ? seo.home_title_ar : seo.home_title_en) || "";
  const homeDesc = (isAr ? seo.home_desc_ar : seo.home_desc_en) || "";
  const homeKeywords = (isAr ? seo.home_keywords_ar : seo.home_keywords_en) || "";
  const canonicalBase =
    (seo.canonical_base || "").trim().replace(/\/$/, "") ||
    (typeof window !== "undefined" ? window.location.origin : "https://startupgate.sa");
  const currentPath = path || (typeof window !== "undefined" ? window.location.pathname : "/");
  const isHome = currentPath === "/";
  // On the homepage the admin-managed copy wins over the page's own props so
  // Settings → SEO is the single source of truth for the landing snippet.
  const rawTitle = isHome && homeTitle
    ? homeTitle
    : title
      ? (title.includes(brand) ? title : `${title} — ${brand}`)
      : homeTitle || `${brand} | Saudi Business Operating System`;
  // Search engines truncate around 60 characters; keep the brand readable.
  const fullTitle = clamp(rawTitle, TITLE_MAX);
  const desc = clamp(
    (isHome && homeDesc) ||
    description ||
    homeDesc ||
    "The Saudi Gate — one business operating system for market entry, shared services, and advisory across KSA and the wider MEA region.",
    DESC_MAX,
  );
  const canonical = `${canonicalBase}${currentPath}`;
  const ogImage =
    image ||
    seo.og_image_url ||
    content?.logo_url ||
    `${canonicalBase}/logo_transparent.png`;
  const themeColor = "#0b1220";
  const twitterHandle = (seo.twitter_handle || "").trim();
  const blocked = noindex || !!seo.noindex_site;
  // index.html ships static description/og/twitter tags so non-JS social
  // scrapers get something. Once React takes over we drop them, otherwise the
  // document would carry two of each and crawlers pick the generic one.
  useEffect(() => {
    document
      .querySelectorAll('[data-static-seo="true"]')
      .forEach((el) => el.parentNode && el.parentNode.removeChild(el));
  }, []);

  const defaultKeywords =
    homeKeywords ||
    "Saudi Arabia market entry, KSA business setup, Saudi Vision 2030, business operating system, shared services KSA, Riyadh company formation, MEA advisory, Saudi Arabia expansion, The Saudi Gate";

  return (
    <Helmet>
      <html lang={lang} dir={lang === "ar" ? "rtl" : "ltr"} />
      <title>{fullTitle}</title>
      <meta name="description" content={desc} />
      <meta name="keywords" content={keywords || defaultKeywords} />
      <meta name="author" content={brand} />
      <meta name="theme-color" content={themeColor} />
      <link rel="canonical" href={canonical} />
      {blocked && <meta name="robots" content="noindex, nofollow" />}
      {!blocked && (
        <meta
          name="robots"
          content="index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1"
        />
      )}

      {/* hreflang alternates. The site serves both languages on the same
          path, so the alternates carry an explicit ?lang= switch that i18n
          reads on load — otherwise all three would be the same URL, which
          search engines ignore. */}
      <link rel="alternate" href={`${canonicalBase}${currentPath}?lang=en`} hrefLang="en" />
      <link rel="alternate" href={`${canonicalBase}${currentPath}?lang=ar`} hrefLang="ar" />
      <link rel="alternate" href={`${canonicalBase}${currentPath}`} hrefLang="x-default" />

      {/* Open Graph */}
      <meta property="og:site_name" content={brand} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={desc} />
      <meta property="og:type" content={type} />
      <meta property="og:url" content={canonical} />
      <meta property="og:image" content={ogImage} />
      <meta property="og:image:alt" content={brand} />
      <meta property="og:locale" content={lang === "ar" ? "ar_SA" : "en_US"} />
      <meta property="og:locale:alternate" content={lang === "ar" ? "en_US" : "ar_SA"} />
      {type === "article" && publishedTime && (
        <meta property="article:published_time" content={publishedTime} />
      )}
      {type === "article" && (modifiedTime || publishedTime) && (
        <meta property="article:modified_time" content={modifiedTime || publishedTime} />
      )}

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      {twitterHandle && <meta name="twitter:site" content={twitterHandle} />}
      {twitterHandle && <meta name="twitter:creator" content={twitterHandle} />}
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={desc} />
      <meta name="twitter:image" content={ogImage} />
      <meta name="twitter:image:alt" content={brand} />

      {(Array.isArray(jsonLd) ? jsonLd : jsonLd ? [jsonLd] : []).map((block, i) => (
        <script key={i} type="application/ld+json">{JSON.stringify(block)}</script>
      ))}
    </Helmet>
  );
}
